import React, { useEffect, useRef, useState, ReactNode } from "react";
import "./AggregatedSelect.scss";

import { isEqual } from "lodash";
import classNames from "classnames";

import { useScrollLock } from "src/app/methods/useScrollLock";
import { SelectOption } from "src/types";
import useOnClickOutside from "../../methods/useOnClickOutside";

import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import DropdownPortal from "../DropdownPortal";
import SelectLabel from "./components/SelectLabel/SelectLabel";
import { AggregatedLabels } from "./components/AggregatedLabels/AggregatedLabels";
import { TooltipedEllipsis } from "../TooltipedEllipsis/TooltipedEllipsis";
import { MenuOverlay } from "./components/MenuOverlay/MenuOverlay";
import Pictogram, { PictogramVariants } from "../Pictogram/Pictogram";

function SelectedLabels({
  multiValue,
  handleLabelClick,
  emptyFieldPlaceholder,
  removeFromValue,
}: {
  multiValue: SelectOption[];
  handleLabelClick: () => void;
  emptyFieldPlaceholder: ReactNode;
  removeFromValue: (index: number) => void;
}) {
  return multiValue?.length > 0 ? (
    <div className="label-selector__selected-labels">
      {multiValue?.map((item, index: number) => (
        <SelectLabel
          key={`${item.value}-${index}`}
          index={index}
          value={item}
          onClickHandler={handleLabelClick}
          emptyFieldPlaceholder={emptyFieldPlaceholder}
          rejectMulti={removeFromValue}
        />
      ))}
    </div>
  ) : null;
}

interface SelectProps {
  optionsData: any;
  uuid: string;
  fieldValue: any;
  updateValue?: any;
  emptyFieldPlaceholder?: ReactNode;
  readOnly?: boolean;
  singleLine?: boolean;
  closeDropdownHandler?: () => void;
  glideGridMode?: boolean;
}

function AggregatedSelect({
  optionsData,
  uuid,
  fieldValue,
  updateValue,
  emptyFieldPlaceholder,
  readOnly,
  singleLine,
  closeDropdownHandler,
  glideGridMode,
}: SelectProps) {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [multiValue, setMultiValue] = useState<SelectOption[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(glideGridMode || false);

  const { lockScroll, unlockScroll } = useScrollLock();

  const dropdownRef = useRef(null);

  const updateMultiValueIfNeeded = () => {
    const newValue = multiValue?.map((el: { value: string }) => el.value);
    const currentValue = fieldValue || [];
    if (!isEqual(currentValue, newValue) && updateValue) {
      updateValue(newValue);
    }
  };

  useOnClickOutside(dropdownRef, () => {
    if (isOpen) {
      setIsOpen(false);
      unlockScroll();
      updateMultiValueIfNeeded();

      if (closeDropdownHandler) {
        closeDropdownHandler();
      }
    }
  });

  useEffect(() => {
    if (optionsData) {
      setOptions(optionsData);
    } else {
      setOptions([]);
    }
  }, [optionsData]);

  useEffect(() => {
    if (options.length) {
      const newValue: SelectOption[] = [];

      fieldValue?.forEach((item: string) => {
        const foundOption = options.find(
          (el: { value: string }) => item === el.value,
        );

        if (foundOption) {
          newValue.push(foundOption);
        }
      });

      setMultiValue(newValue);
    }
  }, [fieldValue, options]);

  const handleLabelClick = () => {
    lockScroll();
    setIsOpen(true);
  };

  const removeFromValue = (index: number) => {
    const newValue = [...multiValue];
    newValue.splice(index, 1);
    setMultiValue(newValue);
  };

  const handleOptionClick = (item: SelectOption) => {
    const newValue = multiValue || [];
    setMultiValue([...newValue, item]);
  };

  const renderValue = () => {
    if (glideGridMode) return <span style={{ height: 0 }} />;

    if (multiValue?.length > 0) {
      return (
        <AggregatedLabels
          multiValue={multiValue}
          setIsOpen={setIsOpen}
          emptyFieldPlaceholder={emptyFieldPlaceholder}
          handleLabelClick={handleLabelClick}
          readOnly={readOnly}
          singleLine={singleLine}
        />
      );
    }
    return (
      <SelectLabel
        value={{}}
        onClickHandler={handleLabelClick}
        emptyFieldPlaceholder={emptyFieldPlaceholder}
        readOnly={readOnly}
      />
    );
  };

  const renderMenuContent = () => (
    <>
      <span className="label-selector__menu-header">
        <IDHFormattedMessage id="ws_selected" defaultMessage="Selected" />
      </span>

      <SelectedLabels
        multiValue={multiValue}
        handleLabelClick={handleLabelClick}
        emptyFieldPlaceholder={emptyFieldPlaceholder}
        removeFromValue={removeFromValue}
      />

      <span className="label-selector__menu-header">
        <IDHFormattedMessage
          id="ws_select_from_the_list"
          defaultMessage="Select from the list"
        />
      </span>

      <div className="drag-n-drop-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className={classNames("options-list")}>
          {options
            ?.sort((a, b) => {
              return a.rank.localeCompare(b.rank);
            })
            ?.map((item: SelectOption) => {
              return (
                <div
                  data-value={item.name}
                  key={item.value}
                  className={classNames("option")}
                  onClick={() => handleOptionClick(item)}
                >
                  <span className="label-selector__label">
                    {item.icon ? (
                      <Pictogram
                        identifier={item.icon.name}
                        variant={
                          item.icon.variant
                            ? item.icon.variant
                            : PictogramVariants.Original
                        }
                      />
                    ) : null}
                    <TooltipedEllipsis content={item.name} maxWidth={210} />
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    </>
  );

  const getWidth = () => {
    if (multiValue?.length >= 2) return 200;
    return "auto";
  };

  if (glideGridMode) {
    return (
      <div
        ref={dropdownRef}
        id={uuid}
        className={classNames("label-selector__wrapper", {
          "label-selector__wrapper--glide-grid-mode": glideGridMode,
        })}
        style={{ width: getWidth() }}
      >
        <MenuOverlay>{renderMenuContent()}</MenuOverlay>
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      id={uuid}
      className="label-selector__wrapper"
      style={{ width: getWidth() }}
    >
      <DropdownPortal
        className="label-selector__dropdown"
        overlay={<MenuOverlay>{renderMenuContent()}</MenuOverlay>}
        visible={isOpen}
      >
        {renderValue()}
      </DropdownPortal>
    </div>
  );
}

export default AggregatedSelect;
